import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import {Panel, PanelHeader} from '@Components/panel';
import {LayoutBackgroundColorOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/layout-background-color-option';
import {LayoutBackgroundColorOpacityOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/layout-background-color-opacity-option';
import PosterEditorColorPanel from '@Panels/poster-editor-color-panel';
import {FillTypes} from '@PosterWhiteboard/classes/fill.class';
import type {RGBA} from '@Utils/color.util';
import {ContextualMenu} from '@Components/contextual-menu';
import {LayoutBackgroundTypeOption} from '@Panels/poster-editor-item-background-panel/components/layout-background-type-option';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetKeyFromLayoutItem} from '@Hooks/poster-editor/useGetKeyFromLayoutItem';

interface LayoutBackgroundPanelProps {
  panelId: string;
  onClose(): void;
}

enum BackgroundPanels {
  DEFAULT = 'DEFAULT',
  COLOR_PICKER = 'COLOR_PICKER',
}

export enum LayoutBackgroundOptionTypes {
  NONE = 'NONE',
  COLOR = 'COLOR',
}

export function LayoutBackgroundPanel({...props}: LayoutBackgroundPanelProps): ReactElement | null {
  const activeItemId = useSingleActivePosterItemId() ?? '';
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const backgroundType = useGetKeyFromLayoutItem('backgroundType');
  const [panelToShow, setPanelToShow] = useState(BackgroundPanels.DEFAULT);
  const [selectedType, setSelectedType] = useState(backgroundType && backgroundType === 1 ? LayoutBackgroundOptionTypes.COLOR : LayoutBackgroundOptionTypes.NONE);
  const activeItemBackgroundColor = useGetKeyFromLayoutItem('itemBackgroundColor');

  useEffect((): void => {
    if (activeItem && 'backgroundType' in activeItem) {
      setSelectedType(backgroundType === 1 ? LayoutBackgroundOptionTypes.COLOR : LayoutBackgroundOptionTypes.NONE);
    }
  }, [backgroundType, activeItem]);

  if (!activeItem || (activeItem && !('backgroundType' in activeItem)) || backgroundType === undefined || !activeItemBackgroundColor) {
    return null;
  }

  const getHeader = (): ReactElement => {
    return (
      <PanelHeader
        onClose={(): void => {
          props.onClose();
        }}
        title={window.i18next.t('pmwjs_background')}
      />
    );
  };

  const updateOpacity = (backgroundColorAlpha: number, undoable = true): void => {
    const backgroundColor = activeItemBackgroundColor.slice() as RGBA;
    backgroundColor[3] = backgroundColorAlpha;
    void activeItem.updateFromObject(
      {
        itemBackgroundColor: backgroundColor,
      },
      {
        undoable,
      }
    );
  };

  const getTypeFromId = (id: LayoutBackgroundOptionTypes): number => {
    switch (id) {
      case LayoutBackgroundOptionTypes.COLOR:
        return 1;
      case LayoutBackgroundOptionTypes.NONE:
      default:
        return 0;
    }
  };

  const updateBackgroundType = (id: LayoutBackgroundOptionTypes): void => {
    if (getTypeFromId(id) === backgroundType) {
      return;
    }

    void activeItem.updateFromObject({
      backgroundType: getTypeFromId(id),
    });
  };

  const getContent = (): ReactElement => {
    return (
      <ContextualMenu id="layout-background-mobile-panel" hasMobileModalPadding hasMobileMorphingProperties>
        {selectedType === LayoutBackgroundOptionTypes.COLOR ? (
          <>
            <LayoutBackgroundColorOpacityOption className="spacing-m-b-4" backgroundColorAlpha={activeItemBackgroundColor[3]} updateOpacity={updateOpacity} />
            <LayoutBackgroundColorOption
              className="spacing-m-b-4"
              backgroundColor={activeItemBackgroundColor}
              onClickColorFill={(): void => {
                setPanelToShow(BackgroundPanels.COLOR_PICKER);
              }}
            />
          </>
        ) : null}

        <LayoutBackgroundTypeOption
          panelId={props.panelId}
          updateBackgroundFillType={updateBackgroundType}
          type={backgroundType === 0 ? LayoutBackgroundOptionTypes.NONE : LayoutBackgroundOptionTypes.COLOR}
        />
      </ContextualMenu>
    );
  };

  const updateColor = (color: RGBA[], undoable = true): void => {
    void activeItem.updateFromObject(
      {
        itemBackgroundColor: color[0],
      },
      {
        undoable,
      }
    );
  };

  const getBackgroundColorPanel = (): ReactElement => {
    return (
      <PosterEditorColorPanel
        panelId={props.panelId}
        fillObject={{
          fillType: FillTypes.SOLID,
          fillColor: [activeItemBackgroundColor],
        }}
        onChangeColor={updateColor}
        onBackButton={(): void => {
          setPanelToShow(BackgroundPanels.DEFAULT);
        }}
        onClose={(): void => {
          props.onClose();
        }}
      />
    );
  };

  const getPanels = (): ReactElement | null => {
    switch (panelToShow) {
      case BackgroundPanels.COLOR_PICKER:
        return getBackgroundColorPanel();
      case BackgroundPanels.DEFAULT:
      default:
        return <Panel panelHeader={getHeader()} panelContent={getContent()} />;
    }
  };

  return getPanels();
}
